/**
 * gestureTracking — la mesure d'un geste des ports, la même dans chaque espace : quatre types
 * `gesture.*`, l'espace lu dans le store au moment du geste. La convention et ses règles :
 * `ports/CLAUDE.md` § « Mesurer un geste ».
 */
import { useCallback, useMemo } from "react"
import { useStore } from "react-redux"

import { type LiveSessionAction } from "../../../../design-system"
import { selectSpace, type Space } from "../../view-models/shared/displayedStudent"
import { track, type TrackedEvent } from "./trackEvent"

export type GesturePhase = "opened" | "submitted" | "succeeded" | "failed"

/**
 * Les gestes nommés — une liste fermée, la valeur de la propriété `gesture` dans Amplitude. Les
 * actions d'une séance y entrent sous leur nom (`move_session`, `report_late`…). Une écriture de
 * `useApiMutation` qui n'en nomme aucun part sous le nom de son service.
 */
export type GestureName =
    | LiveSessionAction
    | "recover_absence"
    | "stay_with_tutor"
    | "extra_hours.request"
    | "subscription.pause"
    | "subscription.pause_cancel"
    | "subscription.extend"
    | "subscription.cancel"
    | "payment.collection_day"
    | "payment.billing_portal"
    | "activity.create"
    | "message.send"
    | "nps.submit"
    | "avatar.generate"
    | "substitution.cancel"
    // Le compte du tuteur
    | "account.avatar"
    | "account.settings"
    | "training.quiz"
    | "invoice.generate"
    | "profile.complete"
    // La dispo et les offres du tuteur
    | "availability.confirm_week"
    | "availability.starting_date"
    | "availability.release"
    | "offer.accept"
    | "offer.hide"
    | "offer.withdraw"
    // Les lives du tuteur
    | "live_preparation.chapter"
    | "live_preparation.subject"
    | "close_out.student_rating"
    | "close_out.student_comment"
    | "close_out.student_voice"
    | "close_out.student_voice_delete"
    | "close_out.platform_rating"
    | "close_out.platform_comment"
    | "close_out.platform_voice"
    | "close_out.platform_voice_delete"
    | "close_out.leave_live"
    | "prescription.give"
    | "prescription.undo"
    | "prescription.regenerate"
    | "prescription.add_activity"
    | "prescription.remove_activity"
    | "prescription.retry_draft"
    // Les activités d'un élève, vues par son tuteur
    | "activity.archive"
    | "activity.restore"
    | "activity.reorder"
    | "activity.submit"
    | "activity.validate"
    | "activity.redo"
    | "activity.edit_title"
    | "activity.remove_quiz"
    | "activity.correction_review"
    | "activity.quiz_feedback"
    | "activity.quiz_signal"
    // La candidature du tuteur
    | "application.profile"
    | "application.answers"
    | "application.video"

export type Gesture = {
    /** Ce que la personne fait : un `GestureName`, ou le nom du service d'une écriture sans nom. */
    gesture: string
    /** L'écran d'où il part, quand il le dit. */
    surface?: string
}

/** Le code d'un refus du serveur, ou `network` quand aucune réponse n'est arrivée. */
export function failureStatus(error: unknown): string {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const status = (error as any)?.response?.status ?? (error as any)?.status
    return typeof status === "number" && status > 0 ? String(status) : "network"
}

export function gestureEvent(
    phase: GesturePhase,
    { gesture, surface }: Gesture,
    space: Space,
    error?: unknown
): TrackedEvent {
    return {
        name: `gesture.${phase}`,
        properties: {
            gesture,
            space,
            ...(surface ? { surface } : {}),
            ...(phase === "failed" ? { status: failureStatus(error) } : {}),
        },
    }
}

export type GestureTracker = {
    opened: () => void
    submitted: () => void
    succeeded: () => void
    failed: (error?: unknown) => void
}

export type GestureEmitter = (phase: GesturePhase, gesture: Gesture, error?: unknown) => void

/** L'émetteur nu, pour une machine qui porte plusieurs gestes : le geste se nomme à chaque émission. */
export function useGestureEmitter(): GestureEmitter {
    const store = useStore()
    return useCallback(
        (phase: GesturePhase, gesture: Gesture, error?: unknown) => {
            // Un événement perdu vaut mieux qu'un geste cassé par sa mesure.
            try {
                track(gestureEvent(phase, gesture, selectSpace(store.getState()), error))
            } catch {
                // rien
            }
        },
        [store]
    )
}

const UNMEASURED: GestureTracker = { opened: () => {}, submitted: () => {}, succeeded: () => {}, failed: () => {} }

/**
 * Les quatre phases d'UN geste ; `null` : un appel qui n'est pas un geste, rien ne part. Le nom
 * d'un service n'y entre que par `useApiMutation`, pour une écriture sans nom métier.
 */
export function useGesture(gesture: GestureName | null, surface?: string): GestureTracker {
    const emitGesture = useGestureEmitter()
    const emit = useCallback(
        (phase: GesturePhase, error?: unknown) => {
            if (gesture != null) emitGesture(phase, { gesture, surface }, error)
        },
        [emitGesture, gesture, surface]
    )
    return useMemo(
        () =>
            gesture == null
                ? UNMEASURED
                : {
                      opened: () => emit("opened"),
                      submitted: () => emit("submitted"),
                      succeeded: () => emit("succeeded"),
                      failed: (error?: unknown) => emit("failed", error),
                  },
        [emit, gesture]
    )
}
